import { Button } from '@workspace/ui/components/Button';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { company } from '~/company/Company';
import { useGlobal } from '~/hooks/useGlobal';
import { useModal } from '~/hooks/useModal';
import { mainStore, orderStore, userStore } from '~/stores';

import { deliveryAddressStore } from '../DeliveryAddressStore';
import {
  saveAuthorizedAddress,
  saveUnauthorizedAddress,
  sendAddressChangedAnalytics,
  sendOnboardingAnalytics,
  sendPostSaveAnalytics,
  updateEtaAndDeliveryMethod,
} from '../saveAddressUtils';

export const SaveButton = observer(
  ({ redirectNext }: { redirectNext?: string | null }) => {
    const { t } = useTranslation();
    const { closeModal } = useModal();
    const { isMobile } = useGlobal();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isPickup =
      searchParams.get('pickup') === 'true' && !!company.pickupAddress;

    const handleCloseModal = () => {
      if (!redirectNext) {
        closeModal();
        return;
      }

      navigate(redirectNext);
    };

    const handleSaveAddress = async () => {
      deliveryAddressStore.setIsLoading(true);
      try {
        const deliveryAddress = toJS(deliveryAddressStore.deliveryAddress);
        const editAddressData = toJS(deliveryAddressStore.editAddressData);

        if (!deliveryAddress?.city) {
          mainStore.pushAlert('error', t('phrases:determineAddress'));
          return;
        }

        if (!deliveryAddressStore.etaCalculation && !isPickup) {
          mainStore.pushAlert(
            'error',
            "Your address isn't in a delivery zone yet",
          );
          return;
        }

        sendAddressChangedAnalytics(
          deliveryAddress,
          deliveryAddressStore.etaCalculation,
        );

        let savedAddress = deliveryAddress;

        if (userStore.isAuthorized) {
          const result = await saveAuthorizedAddress(
            deliveryAddress,
            editAddressData,
            { alwaysSetDeliveryAddress: true },
          );
          savedAddress = result.updatedDeliveryAddress;
        } else {
          saveUnauthorizedAddress(deliveryAddress, editAddressData);
        }

        await updateEtaAndDeliveryMethod(isPickup);

        sendPostSaveAnalytics(savedAddress);

        if (userStore.isFirstLaunch) {
          sendOnboardingAnalytics(
            savedAddress,
            deliveryAddressStore.etaCalculation,
          );

          if (
            !isPickup &&
            (!deliveryAddressStore.deliveryAddress ||
              !(
                deliveryAddressStore.etaCalculation ?? orderStore.etaCalculation
              ))
          ) {
            mainStore.pushAlert(
              'error',
              "Your address isn't in a delivery zone yet",
            );
          }
          handleCloseModal();
          return;
        }

        if (mainStore.deferedDeeplink) {
          navigate(mainStore.deferedDeeplink, {
            state: { isDeeplink: true },
          });
          mainStore.setDeferedDeeplink('');
          return;
        }

        handleCloseModal();
      } finally {
        deliveryAddressStore.setIsLoading(false);
      }
    };

    return (
      <Button
        size="lg"
        variant={company.match({
          mcosmetic: 'brand',
          default: 'primary',
        })}
        disabled={
          !deliveryAddressStore.deliveryAddress ||
          (!company.isBuildingOptional &&
            !deliveryAddressStore.editAddressData.addressBld.trim()) ||
          !deliveryAddressStore.editAddressData.type.trim() ||
          deliveryAddressStore.inputAddressFocused ||
          deliveryAddressStore.isLoading ||
          orderStore.isETALoading ||
          (!userStore.isFirstLaunch &&
            !deliveryAddressStore.etaCalculation &&
            !isPickup)
        }
        onClick={handleSaveAddress}
      >
        {deliveryAddressStore.isLoading ? (
          <span className="spinner _white" />
        ) : (
          t(redirectNext && isMobile ? 'saveAndContinue' : 'deliverHere')
        )}
      </Button>
    );
  },
);
